// Mobile viewport utilities for handling different browser behaviors
import { useEffect } from 'react';

export const detectMobileBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  return {
    isIOS: /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream,
    isAndroid: /android/i.test(userAgent),
    isSafari: /^((?!chrome|android).)*safari/i.test(userAgent),
    isChrome: /chrome/i.test(userAgent) && /google inc/i.test(navigator.vendor),
    isFirefox: /firefox/i.test(userAgent),
    isMobile: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
  };
};

export const getViewportHeight = () => {
  const browser = detectMobileBrowser();
  
  // Use different viewport height strategies based on browser
  if (browser.isIOS && browser.isSafari) {
    // iOS Safari - use -webkit-fill-available
    return window.innerHeight;
  } else if (browser.isAndroid && browser.isChrome) {
    // Android Chrome - use 100dvh if supported, fallback to innerHeight
    if (CSS.supports('height', '100dvh')) {
      return window.innerHeight;
    }
    return window.innerHeight;
  } else {
    // Other browsers - use standard viewport
    return window.innerHeight;
  }
};

export const setViewportHeightCSS = () => {
  const vh = getViewportHeight() * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  
  // Also set safe area variables
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)') || '0px';
  const safeAreaBottom = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)') || '0px';
  
  document.documentElement.style.setProperty('--safe-area-top', safeAreaTop);
  document.documentElement.style.setProperty('--safe-area-bottom', safeAreaBottom);
};

export const handleViewportResize = () => {
  let resizeTimer;
  
  const updateViewport = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      setViewportHeightCSS();
    }, 100);
  };
  
  window.addEventListener('resize', updateViewport);
  window.addEventListener('orientationchange', updateViewport);
  
  // Initial setup
  setViewportHeightCSS();
  
  // Return cleanup function
  return () => {
    window.removeEventListener('resize', updateViewport);
    window.removeEventListener('orientationchange', updateViewport);
    clearTimeout(resizeTimer);
  };
};

export const preventMobileScrollIssues = () => {
  const browser = detectMobileBrowser();
  
  if (browser.isMobile) {
    // Prevent zoom on input focus
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      if (!input.style.fontSize) {
        input.style.fontSize = '16px';
      }
    });
    
    // Prevent pull-to-refresh on mobile
    document.body.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehaviorY = 'none';
    
    // Prevent horizontal scroll
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    
    // Android-specific fixes
    if (browser.isAndroid) {
      // Prevent Android Chrome bounce effect
      document.body.style.overscrollBehavior = 'none';
      
      // Handle Android keyboard
      const handleResize = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      
      window.addEventListener('resize', handleResize);
      handleResize();
    }
  }
};

// Hook for React components
export const useMobileViewport = () => {
  useEffect(() => {
    const cleanup = handleViewportResize();
    preventMobileScrollIssues();
    
    return cleanup;
  }, []);
  
  return {
    browser: detectMobileBrowser(),
    viewportHeight: getViewportHeight()
  };
};